import { Check, Languages, LogOut, Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LANGUAGES, currentLanguage } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { cn, initials } from "@/lib/utils";

const LANGUAGE_NAMES = { de: "Deutsch", it: "Italiano", en: "English" } as const;

export function UserMenu({ collapsed = false }: { collapsed?: boolean }) {
  const { t, i18n } = useTranslation();
  const { user, tenant, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const name = (user?.user_metadata?.full_name as string | undefined) ?? user?.email ?? "";
  const lang = currentLanguage();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex w-full items-center gap-2.5 rounded-lg p-1.5 text-left outline-none hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring/40",
          collapsed && "justify-center",
        )}
      >
        <Avatar className="size-8">
          <AvatarFallback className="bg-muted text-xs font-medium">{initials(name)}</AvatarFallback>
        </Avatar>
        {!collapsed && (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-foreground">{name}</span>
            <span className="block truncate text-xs text-muted-foreground">{tenant?.roles.map((r) => t(`roles.${r}`)).join(" · ")}</span>
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-60">
        <DropdownMenuLabel className="truncate font-normal text-muted-foreground">{user?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Sun className="size-4" /> {t("nav.theme")}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {([
              ["light", Sun, "nav.theme_light"],
              ["dark", Moon, "nav.theme_dark"],
              ["system", Monitor, "nav.theme_system"],
            ] as const).map(([value, Icon, key]) => (
              <DropdownMenuItem key={value} onClick={() => setTheme(value)}>
                <Icon className="size-4" /> {t(key)}
                {theme === value && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages className="size-4" /> {t("nav.language")}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {LANGUAGES.map((code) => (
              <DropdownMenuItem key={code} onClick={() => void i18n.changeLanguage(code)}>
                {LANGUAGE_NAMES[code]}
                {lang === code && <Check className="ml-auto size-4" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void signOut()}>
          <LogOut className="size-4" /> {t("auth.sign_out")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
